import { AsyncPipe, NgClass, NgTemplateOutlet } from "@angular/common";
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, QueryList, signal, ViewChildren } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { TranslateModule } from "@ngx-translate/core";
import {
    Exit,
    PriceChangePlus,
    RemovePriceChangeFromSaleItem,
    SaleItemWithMenu,
    ShopCartSalesEdited
} from "@tilby/tilby-ui-lib/components/tilby-order";
import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";
import { PriceChangeDescriptionObj, TilbyPriceChangePipe } from "@tilby/tilby-ui-lib/pipes/tilby-price-change";
import { Observable } from "rxjs";
import { Departments, SalesItemsPriceChanges } from "tilby-models";
import { ShopCartRowV2Component } from "./shop-cart-row-v2.component";

const FLASH_DURATION_MS = 6000;

@Component({
    selector: "app-shop-cart-v2",
    templateUrl: "./shop-cart-v2.component.html",
    standalone: true,
    imports: [
        AsyncPipe,
        NgClass,
        NgTemplateOutlet,
        MatIconModule,
        MatProgressSpinnerModule,
        TranslateModule,
        TilbyCurrencyPipe,
        TilbyGesturesDirective,
        TilbyPriceChangePipe,
        ShopCartRowV2Component
    ],
    host: { class: "tw-flex tw-h-full tw-flex-col" }
})
export class ShopCartV2Component implements OnInit {
    private readonly elementRef = inject(ElementRef<HTMLElement>);

    @ViewChildren("row", { read: ElementRef }) private readonly rows?: QueryList<ElementRef<HTMLElement>>;

    private _sale?: ShopCartSalesEdited;
    get sale() {
        return this._sale;
    }
    @Input({ required: true }) lastUuidChanged: string | undefined;
    @Input({ required: true }) lastUuidSelected: string | undefined;
    @Input({ required: true }) set sale(sale: ShopCartSalesEdited | undefined) {
        if (!sale) return;
        this._sale = sale;
        this.saleByKey = this.groupByExit(sale.sale_items || []);
        setTimeout(() => this.scrollAndFlashLastChangedItem(sale));
    }
    @Input({ required: true }) departments: Departments[] = [];
    @Input({ required: true }) exitsArray$!: Observable<Exit[]>;
    @Input({ required: true }) groupItemUuid?: string = "";
    @Input({ required: true }) priceChangeDescriptions!: PriceChangeDescriptionObj;
    @Input() ingredientsRemovalAffectsPrice = false;
    @Input() showAllExits = false;
    @Input() disableExitStatus = false;
    @Input() coverConfiguration = { automated_add_cover: undefined, type: undefined, value: undefined };
    @Input() isHidePrices = false;
    @Input() expandAll = false;
    @Input() sentItemUuids: ReadonlySet<string> = new Set();

    @Output() swipeLeftHandlerEmitter = new EventEmitter<SaleItemWithMenu>();
    @Output() swipeRightHandlerEmitter = new EventEmitter<SaleItemWithMenu>();
    @Output() longPressHandlerEmitter = new EventEmitter<SaleItemWithMenu>();
    @Output() removePriceChangeFromSaleItemEmitter = new EventEmitter<RemovePriceChangeFromSaleItem>();
    @Output() removePriceChangeFromSaleEmitter = new EventEmitter<PriceChangePlus>();
    @Output() exitChange = new EventEmitter<Exit>();
    @Output() sendExit = new EventEmitter<Exit>();
    @Output() clickHandlerEmitter = new EventEmitter<{ saleItem: SaleItemWithMenu, isSelected: boolean }>();
    @Output() lastUuidChangedEmitter = new EventEmitter<SaleItemWithMenu>();

    protected saleByKey: Record<number, SaleItemWithMenu[]> = {};
    protected isSelected = false;
    protected clickEnabled = true;
    protected hasCoverConfiguration = false;
    protected readonly expandedUuids = signal<ReadonlySet<string>>(new Set());
    protected readonly flashUuid = signal<string | undefined>(undefined);

    private flashTimeout?: ReturnType<typeof setTimeout>;

    ngOnInit(): void {
        this.hasCoverConfiguration = this.coverConfiguration.automated_add_cover === "on"
            && this.coverConfiguration.type === "id"
            && this.coverConfiguration.value !== undefined;
    }

    protected isCoverItem(saleItem: SaleItemWithMenu): boolean {
        return this.hasCoverConfiguration && saleItem.item_id === this.coverConfiguration.value;
    }

    protected findDepartmentById(departmentId: number): Departments | undefined {
        return this.departments.find(({ id = "" }) => id == departmentId);
    }

    protected toggleExpanded(uuid: string): void {
        this.expandedUuids.update((current) => {
            const next = new Set(current);
            if (!next.delete(uuid)) {
                next.add(uuid);
            }
            return next;
        });
    }

    protected clickHandler(saleItem: SaleItemWithMenu): void {
        if (!this.clickEnabled) {
            return;
        }
        this.isSelected = (saleItem.uuid == this.lastUuidSelected) ? !this.isSelected : true;
        this.clickHandlerEmitter.emit({ saleItem, isSelected: this.isSelected });
    }

    protected swipeLeftHandler(saleItem: SaleItemWithMenu): void {
        this.clickEnabled = false;
        this.swipeLeftHandlerEmitter.emit(saleItem);
    }

    protected swipeRightHandler(saleItem: SaleItemWithMenu): void {
        this.clickEnabled = false;
        this.swipeRightHandlerEmitter.emit(saleItem);
    }

    protected longPressHandler(saleItem: SaleItemWithMenu): void {
        this.clickEnabled = false;
        this.longPressHandlerEmitter.emit(saleItem);
    }

    protected removePriceChangeFromSaleItem(saleItem: SaleItemWithMenu, priceChange: SalesItemsPriceChanges): void {
        this.removePriceChangeFromSaleItemEmitter.emit({ saleItem, priceChange });
    }

    protected enableClick(): void {
        this.clickEnabled = true;
    }

    private groupByExit(items: SaleItemWithMenu[]): Record<number, SaleItemWithMenu[]> {
        const grouped: Record<number, SaleItemWithMenu[]> = {};
        for (const item of items) {
            const key = item.exit || 0;
            (grouped[key] ??= []).push({ ...item, exit: item.exit || undefined });
        }
        return grouped;
    }

    private isInViewport(element: HTMLElement): boolean {
        const rect = element.getBoundingClientRect();
        const host = this.elementRef.nativeElement.getBoundingClientRect();
        return rect.top >= host.top && rect.bottom <= host.bottom;
    }

    private scrollAndFlashLastChangedItem(sale: ShopCartSalesEdited): void {
        clearTimeout(this.flashTimeout);
        this.flashUuid.set(undefined);

        const element = this.rows?.find((row) => row.nativeElement.id === `${this.lastUuidChanged}`)?.nativeElement;
        if (!element) {
            return;
        }

        if (!this.isInViewport(element)) {
            element.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
        }

        this.flashUuid.set(this.lastUuidChanged);
        this.flashTimeout = setTimeout(() => this.flashUuid.set(undefined), FLASH_DURATION_MS);

        const changed = sale.sale_items?.find((item) => item.uuid === `${this.lastUuidChanged}`);
        if (changed) {
            this.lastUuidChangedEmitter.emit(changed);
        }
    }
}
